/**
 * Whisper 음성 인식 서비스
 * 파일 업로드 및 실시간 스트리밍 음성 인식을 처리합니다.
 */
import { pipeline } from "@huggingface/transformers";

class WhisperService {
  constructor() {
    this.pipeline = null;
    this.isInitialized = false;
    this.model = "onnx-community/whisper-small";
  }

  /**
   * Whisper 모델을 초기화합니다.
   */
  async initialize() {
    if (this.isInitialized) {
      return this.pipeline;
    }

    try {
      console.log('🚀 Whisper 모델 로딩 시작...');
      
      this.pipeline = await pipeline(
        "automatic-speech-recognition",
        this.model,
        {
          device: 'webgpu', // WebGPU를 먼저 시도, 실패하면 CPU로 fallback
          dtype: 'fp16'
        }
      );
      
      this.isInitialized = true;
      console.log('✅ Whisper 모델 로딩 완료');
      
      return this.pipeline;
    } catch (error) {
      console.error('❌ Whisper 모델 로딩 실패:', error);
      throw new Error(`Whisper 모델 로딩 실패: ${error.message}`);
    }
  }

  /**
   * 오디오 파일을 텍스트로 변환합니다.
   * @param {File|Blob} audioFile - 변환할 오디오 파일
   * @param {Object} options - 변환 옵션
   * @returns {Promise<string>} 변환된 텍스트
   */
  async transcribeFile(audioFile, options = {}) {
    if (!this.isInitialized || !this.pipeline) {
      throw new Error('Whisper 모델이 초기화되지 않았습니다.');
    }

    try {
      const audioUrl = URL.createObjectURL(audioFile);
      
      const result = await this.pipeline(audioUrl, {
        task: 'transcribe',
        language: options.language || 'ja',
        // return_timestamps: options.return_timestamps || false,
        without_timestamps: true,               // 타임스탬프 비사용
        return_timestamps: false,
        // 문맥 유지용 긴 청크 + 오버랩
        chunk_length_s: options.chunk_length_s ?? 30,
        stride_length_s: options.stride_length_s ?? 5,
        // 디코딩 안정화
        temperature: options.temperature ?? 0,  // 결정적 디코딩(잡음↓)
        // beam search (속도↓ 정확도↑)
        num_beams: options.num_beams ?? 10,
        // 일부 구현에서 지원될 수 있는 옵션 (지원 안 해도 무해)
        // condition_on_previous_text: true,
        // suppress_tokens: [-1], // (라이브러리에 따라 다름)
        ...options
      });

      URL.revokeObjectURL(audioUrl);
      
      return result.text;
    } catch (error) {
      console.error('❌ 오디오 변환 실패:', error);
      throw new Error(`오디오 변환 실패: ${error.message}`);
    }
  }

  /**
   * 실시간 오디오 스트림을 텍스트로 변환합니다.
   * @param {MediaStream} stream - 오디오 스트림
   * @param {Function} onResult - 결과 콜백 함수
   * @param {Object} options - 변환 옵션
   * @returns {Object} 컨트롤 객체 (stop 메서드 포함)
   */
  async transcribeStream(stream, onResult, options = {}) {
    if (!this.isInitialized || !this.pipeline) {
      throw new Error('Whisper 모델이 초기화되지 않았습니다.');
    }

    let mediaRecorder;
    let audioChunks = [];
    let isRecording = true;

    try {
      mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunks.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        if (audioChunks.length > 0) {
          const audioBlob = new Blob(audioChunks, { 
            type: 'audio/webm;codecs=opus' 
          });
          
          try {
            const text = await this.transcribeFile(audioBlob, options);
            if (text.trim() && onResult) {
              onResult(text);
            }
          } catch (error) {
            console.error('❌ 스트림 변환 오류:', error);
            if (onResult) {
              onResult(null, error);
            }
          }
        }
        audioChunks = [];
      };

      // 2초마다 오디오 청크 처리
      const chunkInterval = options.chunkInterval || 2000;
      mediaRecorder.start(chunkInterval);

      return {
        stop: () => {
          isRecording = false;
          if (mediaRecorder && mediaRecorder.state !== 'inactive') {
            mediaRecorder.stop();
          }
          if (stream) {
            stream.getTracks().forEach(track => track.stop());
          }
        },
        isRecording: () => isRecording
      };

    } catch (error) {
      console.error('❌ 스트림 변환 시작 실패:', error);
      throw new Error(`스트림 변환 시작 실패: ${error.message}`);
    }
  }

  /**
   * 마이크 접근 권한을 요청하고 스트림을 생성합니다.
   * @param {Object} constraints - 오디오 제약 조건
   * @returns {Promise<MediaStream>} 오디오 스트림
   */
  async getMicrophoneStream(constraints = {}) {
    try {
      const defaultConstraints = {
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          ...constraints.audio
        }
      };

      const stream = await navigator.mediaDevices.getUserMedia(defaultConstraints);
      return stream;
    } catch (error) {
      console.error('❌ 마이크 접근 실패:', error);
      throw new Error(`마이크 접근 실패: ${error.message}`);
    }
  }

  /**
   * 지원되는 오디오 형식을 확인합니다.
   * @returns {Object} 지원되는 형식 정보
   */
  getSupportedFormats() {
    const formats = {
      'audio/webm;codecs=opus': MediaRecorder.isTypeSupported('audio/webm;codecs=opus'),
      'audio/mp4': MediaRecorder.isTypeSupported('audio/mp4'),
      'audio/wav': MediaRecorder.isTypeSupported('audio/wav'),
      'audio/ogg': MediaRecorder.isTypeSupported('audio/ogg')
    };

    return formats;
  }

  /**
   * 서비스 정리
   */
  destroy() {
    this.pipeline = null;
    this.isInitialized = false;
  }
}

// 싱글톤 인스턴스 생성
const whisperService = new WhisperService();

export default whisperService;
